import React, { useRef, useMemo } from 'react';

import { ChatMessage } from '../../services/chatService';

import { StreamingRefsFull, IAbortController } from './types';
import { useTimeHelper } from './useTimeHelper';

const useUIRefs = (initialMessages: ChatMessage[]) => {
  const messagesRef = useRef<ChatMessage[]>(initialMessages);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(true);

  return useMemo(() => ({
    messagesRef, inputRef, messagesEndRef, chatContainerRef, shouldAutoScrollRef
  }), []);
};

const useTokenRefs = () => {
  const tokenQueueRef = useRef<string[]>([]);
  const tokenIntervalRef = useRef<number | null>(null);
  const pendingCompletionRef = useRef<{ finalizeCallback: () => void } | null>(null);

  return useMemo(() => ({ tokenQueueRef, tokenIntervalRef, pendingCompletionRef }), []);
};

const useStreamStateRefs = () => {
  const streamControllerRef = useRef<IAbortController | null>(null);
  const streamingBufferRef = useRef('');
  const streamingMessageIdRef = useRef<string | null>(null);
  const titleGenerationBufferRef = useRef('');
  const hasTriggeredInitialAIRef = useRef(false);
  const isUserStreamingRef = useRef(false);

  return useMemo(() => ({
    streamControllerRef, streamingBufferRef, streamingMessageIdRef,
    titleGenerationBufferRef, hasTriggeredInitialAIRef, isUserStreamingRef
  }), []);
};

const useStreamingRefs = () => {
  const tokenRefs = useTokenRefs();
  const stateRefs = useStreamStateRefs();

  return useMemo(() => ({ ...tokenRefs, ...stateRefs }), [tokenRefs, stateRefs]);
};

//
type CombinedRefs = ReturnType<typeof useUIRefs> & 
  ReturnType<typeof useTokenRefs> & 
  ReturnType<typeof useStreamStateRefs> & 
  { messageOrderRef: React.MutableRefObject<number> };

const createRefsFullLogic = (refs: CombinedRefs, incrementMessageOrder: () => number): StreamingRefsFull => ({
  ...refs,
  pushTokens: (t: string[]) => refs.tokenQueueRef.current.push(...t),
  shiftToken: () => refs.tokenQueueRef.current.shift(),
  setTokenInterval: (id: number) => { refs.tokenIntervalRef.current = id; },
  clearTokenInterval: () => {
    if (refs.tokenIntervalRef.current) {
      clearInterval(refs.tokenIntervalRef.current);
      refs.tokenIntervalRef.current = null;
    }
  },
  clearTokenQueue: () => { refs.tokenQueueRef.current = []; },

  //
  setPendingCompletion: (val: { finalizeCallback: () => void } | null) => { refs.pendingCompletionRef.current = val; },
  abortStream: () => refs.streamControllerRef.current?.abort(),
  //
  setStreamController: (val: IAbortController | null) => { refs.streamControllerRef.current = val; },
  setStreamingBuffer: (val: string) => { refs.streamingBufferRef.current = val; },
  appendToBuffer: (val: string) => { refs.streamingBufferRef.current += val; },
  setStreamingMessageId: (val: string | null) => { refs.streamingMessageIdRef.current = val; },
  setTitleGenerationBuffer: (val: string) => { refs.titleGenerationBufferRef.current = val; },
  setIsUserStreaming: (val: boolean) => { refs.isUserStreamingRef.current = val; },
  setHasTriggeredInitialAI: (val: boolean) => { refs.hasTriggeredInitialAIRef.current = val; },
  setShouldAutoScroll: (val: boolean) => { refs.shouldAutoScrollRef.current = val; },
  focusInput: () => refs.inputRef.current?.focus(),
  incrementMessageOrder
});

export const useChatRefs = (initialMessages: ChatMessage[]) => {
  const uiRefs = useUIRefs(initialMessages);
  const streamRefs = useStreamingRefs();
  const { messageOrderRef, incrementMessageOrder, getOrderedTimestamp } = useTimeHelper();

  const refsFull: StreamingRefsFull = useMemo(() => createRefsFullLogic(
    { ...uiRefs, ...streamRefs, messageOrderRef },
    incrementMessageOrder
  ), [uiRefs, streamRefs, incrementMessageOrder, messageOrderRef]);

  return {
    refsFull,
    messagesRef: uiRefs.messagesRef,
    getOrderedTimestamp,
    inputRef: uiRefs.inputRef,
    messagesEndRef: uiRefs.messagesEndRef,
    chatContainerRef: uiRefs.chatContainerRef,
    shouldAutoScrollRef: uiRefs.shouldAutoScrollRef
  };
};
